
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const ProcessSection = () => {
  const steps = [
    {
      number: '01',
      title: '在线测试适配性',
      description: '通过我们的智能测试系统，3分钟快速评估您的业务是否适合1039市场采购贸易模式，获取专业的适配度分析报告。',
      image: 'https://public.readdy.ai/ai/img_res/3c9eca801488d204e5d88f0e60a1b4f3.jpg',
      cta: '开始测试',
      ctaLink: '/test',
      ctaStyle: 'primary',
    },
    {
      number: '02',
      title: '使用1039数字化系统',
      description: '注册并使用我们的一站式数字化平台，完成企业备案、商品申报、组货拼柜等全流程操作，系统自动生成报关单据。',
      image: 'https://readdy.ai/api/search-image?query=Digital%20platform%20dashboard%20interface%20showing%20logistics%20and%20trade%20management%20system%2C%20modern%20UI%20design%20with%20data%20visualization%20charts%20and%20workflow%20diagrams%2C%20clean%20professional%20illustration%20with%20blue%20and%20yellow%20color%20scheme%2C%20showing%20multiple%20connected%20modules%20and%20real-time%20data&width=400&height=300&seq=step-02&orientation=landscape',
      cta: '了解更多',
      ctaLink: '#system',
      ctaStyle: 'secondary',
    },
    {
      number: '03',
      title: '完成出口与结汇',
      description: '通过海关监管系统完成报关出口，享受便捷的通关服务。出口完成后，快速办理外汇结汇，资金安全回笼到账。',
      image: 'https://readdy.ai/api/search-image?query=International%20shipping%20and%20currency%20exchange%20concept%20illustration%2C%20showing%20cargo%20containers%2C%20airplane%2C%20and%20currency%20symbols%20with%20flowing%20arrows%2C%20modern%20minimalist%20design%20with%20blue%20and%20yellow%20accents%2C%20professional%20business%20infographic%20style%20with%20clean%20background&width=400&height=300&seq=step-03&orientation=landscape',
      cta: '了解更多',
      ctaLink: '#',
      ctaStyle: 'secondary',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-primary via-primary-dark to-primary relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-[1440px] mx-auto px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-3 h-3 bg-accent rotate-45"></div>
            <span className="text-accent text-sm font-semibold uppercase tracking-wider whitespace-nowrap">简单三步</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">
            从测试到出口的完整路径
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            我们为您提供全流程数字化服务，让出口变得简单高效
          </p>
        </motion.div>

        {/* Process Steps */}
        <div className="grid grid-cols-3 gap-8 relative">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="relative"
            >
              {/* Connecting Arrow */}
              {index < steps.length - 1 && (
                <div className="absolute top-24 -right-4 z-0 hidden lg:block">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.2 + 0.5 }}
                  >
                    <svg width="80" height="40" viewBox="0 0 80 40" fill="none">
                      <path
                        d="M0 20 L60 20 M60 20 L50 10 M60 20 L50 30"
                        stroke="#FFB800"
                        strokeWidth="2"
                        strokeDasharray="5,5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </motion.div>
                </div>
              )}

              {/* Card */}
              <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 h-full flex flex-col shadow-2xl hover:shadow-accent/20 transition-all duration-300 relative z-10">
                {/* Number */}
                <div className="text-8xl font-thin text-accent mb-6 leading-none">
                  {step.number}
                </div>

                {/* Image */}
                <div className="mb-6 rounded-xl overflow-hidden">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full h-48 object-cover"
                  />
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-primary mb-4">
                  {step.title}
                </h3>

                {/* Description */}
                <p className="text-neutral-500 leading-relaxed mb-6 flex-grow">
                  {step.description}
                </p>

                {/* CTA Button */}
                {step.ctaStyle === 'primary' ? (
                  <Link
                    to={step.ctaLink}
                    className="w-full py-4 bg-gradient-to-r from-accent to-accent-dark text-primary font-bold rounded-xl hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    {step.cta}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                ) : (
                  <a
                    href={step.ctaLink}
                    className="w-full py-4 border-2 border-primary text-primary font-bold rounded-xl hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    {step.cta}
                    <i className="ri-arrow-right-line"></i>
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
